import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clients as clientsApi, wizard, intakes, zarzadyZlewni, geo, savedParcels,
         type ZarzadZlewniDto } from '@/api/endpoints';
import type { Client, AuthorityProposal, Intake } from '@/types';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { ClientFormModal } from '@/components/ClientFormModal';
import { MapLibreMap } from '@/components/MapLibreMap';
import { CoordsInput, sanitizeDecimalInput } from '@/components/CoordsInput';
import { IntakeDocumentExtractModal } from '@/components/IntakeDocumentExtractModal';
import { VOIVODESHIPS, COUNTIES } from '@/data/poland-geo';
import { gminyFor, resolveGmina } from '@/data/poland-gmina';
import { boreholes as boreholesApi, lithology } from '@/api/endpoints';
import type { ExtractedIntakeData, ExtractedBoreholeData, LithologyType } from '@/api/endpoints';
import { nextFreeDesignation, normKey, repeatedIndexes } from '@/lib/duplicates';

// latitude/longitude trzymane jako string (pozwala wpisać kropkę/część dziesiętną w trakcie
// pisania); na number konwertujemy dopiero przy zapisie.
type PlannedBorehole = { designation: string; function: string; depth?: number;
                         latitude?: string; longitude?: string };

/** Działka wykryta z współrzędnych (ULDK) — pokazywana w kreatorze i zapisywana po utworzeniu ujęcia. */
type DetectedParcel = {
  egibId: string;
  parcelNumber: string | null;
  obreb: string | null;
  geometry: { type: 'Polygon' | 'MultiPolygon'; coordinates: unknown } | null;
};

/** "Szczecinek 20" → ["Szczecinek", "20"] (nazwa obrębu + numer). Numer opcjonalny. */
function splitObreb(obreb: string | null): [string | null, string | null] {
  if (!obreb) return [null, null];
  const m = obreb.match(/^(.*?)[\s]+(\d+\w*)$/);
  return m ? [m[1].trim() || null, m[2]] : [obreb.trim() || null, null];
}

const STEPS = ['Klient', 'Lokalizacja', 'Wydajność i decydent', 'Planowane otwory', 'Podsumowanie'];

export function IntakeWizard() {
  const nav = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [clientId, setClientId] = useState<number | ''>('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'NEW' | 'EXISTING'>('NEW');
  const [town, setTown] = useState('');
  const [county, setCounty] = useState('');
  const [voivodeship, setVoivodeship] = useState('');
  const [gmina, setGmina] = useState('');
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoInfo, setGeoInfo] = useState<string | null>(null);
  const [detectedParcel, setDetectedParcel] = useState<DetectedParcel | null>(null);
  // Sekwencja żądań geokodowania — tylko najnowsza odpowiedź zapisuje stan (out-of-order guard).
  const geoReqSeq = useRef(0);
  const [latitude, setLatitude] = useState<string>('');
  const [longitude, setLongitude] = useState<string>('');
  const [q, setQ] = useState<number | ''>('');   // Q opcjonalne — '' = nie podano
  const [proposal, setProposal] = useState<AuthorityProposal | null>(null);
  const [decydentOverride, setAuthorityOverride] = useState<string>('');
  const [justification, setUzasadnienie] = useState<string>('');
  const [boreholes, setBoreholes] = useState<PlannedBorehole[]>([{ designation: 'H-01', function: 'PRODUCTION' }]);
  const [err, setErr] = useState<string | null>(null);
  // Ujęcie o tej nazwie, które ten klient już ma. Sama podpowiedź — zapis blokuje serwer.
  const [nameTaken, setNameTaken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [extractModalOpen, setExtractModalOpen] = useState(false);
  // Pola pre-filled przez AI — UI podświetla je badge'em "AI". Zerowane gdy user
  // wyczyści/zmieni dane ręcznie (na razie tylko po pre-fill, bez resetu per-field).
  const [aiFilledFields, setAiFilledFields] = useState<Set<string>>(new Set());
  // Pełne dane otworów wyciągnięte przez AI — czekają na zapis ich detali (konstrukcja,
  // hydraulika, layers) PO utworzeniu ujęcia. Wizard.save tworzy minimum boreholes; te
  // pola backfillujemy potem przez PUT /api/boreholes/{id} + POST .../layers.
  const [pendingBoreholeDetails, setPendingBoreholeDetails] = useState<ExtractedBoreholeData[]>([]);
  const [zzList, setZzList] = useState<ZarzadZlewniDto[]>([]);
  const [zarzadZlewniId, setZarzadZlewniId] = useState<number | ''>('');
  const [zzDetectInfo, setZzDetectInfo] = useState<string | null>(null);

  useEffect(() => { clientsApi.list().then(setClients).catch(() => { /* nonfatal */ }); }, []);
  useEffect(() => { zarzadyZlewni.list().then(setZzList).catch(() => { /* nonfatal */ }); }, []);

  const detectZarzadZlewni = async () => {
    const lat = parseFloat(latitude); const lon = parseFloat(longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
      setZzDetectInfo('Najpierw wprowadź współrzędne ujęcia.');
      return;
    }
    try {
      const res = await zarzadyZlewni.byCoords(lat, lon, voivodeship || undefined);
      if (res.match) {
        setZarzadZlewniId(res.match.id);
        setZzDetectInfo(`Wykryto: ${res.match.name}. ${res.reason}`);
      } else {
        setZzDetectInfo(res.reason + (res.candidates.length > 0
          ? ` Kandydaci: ${res.candidates.map(c => c.name).join(', ')}.` : ''));
      }
    } catch (e) {
      setZzDetectInfo('Błąd: ' + (e instanceof Error ? e.message : String(e)));
    }
  };

  useEffect(() => {
    // Q opcjonalne — bez wartości nie ma automatycznej propozycji decydenta.
    if (q === '') { setProposal(null); return; }
    const h = setTimeout(() => {
      wizard.proposal(q).then(setProposal).catch(() => { /* nonfatal */ });
    }, 300);
    return () => clearTimeout(h);
  }, [q]);

  const effectiveAuthority = decydentOverride || proposal?.authority || '';
  const propozycjaAuthority = proposal?.authority;
  const nadpisano = decydentOverride && propozycjaAuthority && decydentOverride !== propozycjaAuthority;

  const qPct = Math.min(100, q === '' ? 0 : q);

  // Syntetyczny intake do MapLibreMap — pojedynczy borehole na pozycji wpisanych współrzędnych.
  // useMemo żeby mapa nie re-mountowała się przy każdym renderze.
  const previewIntake = useMemo<Intake | null>(() => {
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    return ({
      id: -1,
      number: 'preview',
      name: name || 'Nowe ujęcie',
      latitude: lat,
      longitude: lng,
      qM3h: q === '' ? null : q,
      boreholes: [{
        id: -1,
        designation: 'PREVIEW',
        function: 'PRODUCTION',
        functionName: 'Eksploatacyjny',
        functionAbbreviation: 'E',
        status: 'PLANNED',
        latitude: lat,
        longitude: lng,
        layers: [],
        housing: null,
      }],
    } as unknown as Intake);
  }, [latitude, longitude, name, q]);

  const previewCenter = useMemo<[number, number] | undefined>(() => {
    if (!previewIntake) return undefined;
    return [Number(previewIntake.longitude), Number(previewIntake.latitude)];
  }, [previewIntake]);

  const addBorehole = () => setBoreholes([...boreholes,
    { designation: nextFreeDesignation(boreholes.map(o => o.designation)), function: 'PRODUCTION' }]);
  // Powtórzone oznaczenia w obrębie listy — serwer je odrzuci, więc pokazujemy je od razu przy wierszu.
  const repeatedBoreholes = useMemo(() => repeatedIndexes(boreholes.map(o => o.designation)), [boreholes]);
  const rmBorehole = (i: number) => setBoreholes(boreholes.filter((_, idx) => idx !== i));
  const updBorehole = (i: number, patch: Partial<PlannedBorehole>) =>
    setBoreholes(boreholes.map((o, idx) => idx === i ? { ...o, ...patch } : o));

  /**
   * Odwrotne geokodowanie: uzupełnia miejscowość/województwo/powiat/gminę oraz pobiera numer
   * działki (zapisywanej po utworzeniu ujęcia) z wprowadzonych współrzędnych. Wywoływane ręcznie
   * (przycisk) oraz automatycznie po wpisaniu poprawnych współrzędnych (debounce niżej).
   */
  const detectAddress = async (coords?: { lat: number; lng: number }, opts?: { fillOnlyEmpty?: boolean }) => {
    const lat = coords?.lat ?? parseFloat(latitude);
    const lng = coords?.lng ?? parseFloat(longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      setGeoInfo('Najpierw wprowadź współrzędne ujęcia.'); return;
    }
    const onlyEmpty = opts?.fillOnlyEmpty ?? false;
    const reqId = ++geoReqSeq.current;   // starsze odpowiedzi porzucamy (out-of-order)
    setGeoBusy(true); setGeoInfo(null);
    try {
      const r = await geo.reverse(lat, lng);
      if (reqId !== geoReqSeq.current) return;   // pojawiło się nowsze żądanie — nie nadpisuj
      if (!r) {
        setGeoInfo('Nie znaleziono działki w tym punkcie (poza terytorium lub brak danych EGiB).');
        setDetectedParcel(null);
        return;
      }
      // Pola adresowe: przy auto-podpowiedzi (onlyEmpty) nie nadpisujemy ręcznie/AI wpisanych
      // wartości. Funkcyjne settery czytają aktualny stan (bez nieaktualnego domknięcia z debounce'a).
      const rv = r.voivodeship, rc = r.county, rt = r.town;
      if (rv) setVoivodeship(prev => (onlyEmpty && prev ? prev : rv));
      if (rc) setCounty(prev => (onlyEmpty && prev ? prev : rc));
      setGmina(prev => {
        const g = rv && rc ? resolveGmina(rv, rc, r.gmina) : (r.gmina ?? '');
        return onlyEmpty && prev ? prev : g;
      });
      if (rt) setTown(prev => (onlyEmpty && prev ? prev : rt));
      // Działka wynika ze współrzędnych (nie jest wpisywana ręcznie) — zawsze ją ustawiamy.
      setDetectedParcel(r.egibId
        ? { egibId: r.egibId, parcelNumber: r.parcelNumber, obreb: r.obreb, geometry: r.geometry }
        : null);
      const dzialka = [r.parcelNumber && `dz. ${r.parcelNumber}`, r.obreb && `obręb ${r.obreb}`].filter(Boolean).join(', ');
      setGeoInfo('Wykryto: ' + [r.town, r.gmina, r.county, r.voivodeship].filter(Boolean).join(', ')
        + (dzialka ? ' · ' + dzialka : '') + (r.source ? ` (${r.source})` : ''));
    } catch (e) {
      if (reqId !== geoReqSeq.current) return;
      setGeoInfo('Błąd geokodowania: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      if (reqId === geoReqSeq.current) setGeoBusy(false);
    }
  };

  // Auto-podpowiedź: po wpisaniu poprawnych współrzędnych (w obszarze PL) sam wykrywa adres i
  // numer działki — debounce 700 ms, żeby nie strzelać przy każdym znaku.
  useEffect(() => {
    const lat = parseFloat(latitude); const lng = parseFloat(longitude);
    // Współrzędne się zmieniły — wcześniej wykryta działka już nie pasuje; porzucamy ją, żeby nie
    // została pokazana ani zapisana gdy współrzędne są puste/niepełne albo przed nową detekcją.
    setDetectedParcel(null);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    if (lat < 48 || lat > 56 || lng < 13 || lng > 25) return;
    // Nie strzelamy dla niepełnych wpisów w trakcie pisania — wymagamy precyzji ~działkowej
    // (min. 3 miejsca po kropce). Ręczny przycisk działa też dla zgrubnych współrzędnych.
    if (!/\.\d{3,}/.test(latitude) || !/\.\d{3,}/.test(longitude)) return;
    const h = setTimeout(() => { detectAddress({ lat, lng }, { fillOnlyEmpty: true }); }, 700);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latitude, longitude]);

  // Nazwa ujęcia jest unikalna w obrębie klienta. Pytamy o to przy wpisywaniu, żeby kolizja
  // wyszła przy polu, a nie dopiero po kliknięciu „Utwórz ujęcie" na końcu formularza.
  useEffect(() => {
    if (!clientId || !name.trim()) { setNameTaken(null); return; }
    let porzucone = false;
    const h = setTimeout(() => {
      intakes.nameCheck(Number(clientId), name)
        .then(r => { if (!porzucone) setNameTaken(r.taken ? (r.number ?? '—') : null); })
        .catch(() => { if (!porzucone) setNameTaken(null); });
    }, 400);
    return () => { porzucone = true; clearTimeout(h); };
  }, [clientId, name]);

  const submit = async () => {
    if (!clientId || !name) { setErr('Uzupełnij nazwę i klienta.'); return; }
    if (repeatedBoreholes.size > 0) {
      setErr('Powtórzone oznaczenia otworów — każdy otwór w ujęciu musi mieć inne oznaczenie.');
      return;
    }
    if (nadpisano && !justification.trim()) { setErr('Podaj justification nadpisania decydenta.'); return; }
    setBusy(true);
    setErr(null);
    try {
      const created = await intakes.create({
        name,
        clientId: Number(clientId),
        q: q === '' ? undefined : q,
        kind,
        town: town || undefined,
        county: county || undefined,
        voivodeship: voivodeship || undefined,
        gmina: gmina || undefined,
        latitude: latitude ? Number(latitude) : undefined,
        longitude: longitude ? Number(longitude) : undefined,
        decydentOverride: decydentOverride || undefined,
        justification: justification || undefined,
        zarzadZlewniId: zarzadZlewniId === '' ? null : Number(zarzadZlewniId),
        otworyPlanowane: boreholes.filter(o => o.designation?.trim()).map(o => ({
          designation: o.designation.trim(),
          function: o.function,
          depth: o.depth,
          latitude: o.latitude && Number.isFinite(Number(o.latitude)) ? Number(o.latitude) : undefined,
          longitude: o.longitude && Number.isFinite(Number(o.longitude)) ? Number(o.longitude) : undefined,
        })),
      });

      // Działka wykryta z współrzędnych — zapisz ją do ujęcia (jak zapis z mapy w szczegółach).
      // Zapisujemy tylko gdy współrzędne są nadal ustawione (działka pasuje do nich); niepowodzenie
      // nie blokuje nawigacji: ujęcie już powstało, działkę można dodać z mapy.
      if (detectedParcel?.egibId && latitude && longitude) {
        try {
          const [obrebNazwa, obrebNumer] = splitObreb(detectedParcel.obreb);
          await savedParcels.add(created.id, {
            egibId: detectedParcel.egibId,
            parcelNumber: detectedParcel.parcelNumber,
            obrebNazwa,
            obrebNumer,
            gminaNazwa: gmina || null,
            geometry: detectedParcel.geometry ?? undefined,
          });
        } catch (e) {
          console.warn('Zapis działki z współrzędnych nieudany:', e);
        }
      }

      // Jeśli AI wyciągnęła dodatkowe dane otworów (konstrukcja, hydraulika, profil
      // litologiczny) — backfill ich teraz przez PUT /boreholes/{id} + POST .../layers.
      if (pendingBoreholeDetails.length > 0) {
        try {
          await backfillBoreholeDetails(created.id, pendingBoreholeDetails);
        } catch (e) {
          console.warn('Backfill borehole details failed:', e);
          // Nie blokujemy nawigacji — ujęcie zostało utworzone, geolog uzupełni manualnie.
        }
      }
      nav('/intakes/' + created.id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  /** Po utworzeniu intake — dopisz pełne dane otworów + warstwy litologiczne. */
  async function backfillBoreholeDetails(intakeId: number, details: ExtractedBoreholeData[]) {
    // Pobierz listę otworów które wizard.save utworzył (mamy id-y)
    const created = await boreholesApi.listForIntake(intakeId);
    // Pobierz katalog litologii do fuzzy-match po symbolu
    let lithCatalog: LithologyType[] = [];
    try { lithCatalog = await lithology.list(); } catch { /* nonfatal */ }

    for (const det of details) {
      // Dopasowanie po znormalizowanym oznaczeniu, nie po dosłownym. Serwer przycina oznaczenie
      // przy zapisie, więc odczyt AI z końcową spacją („H-1 ") nie trafiłby w zapisane „H-1"
      // i cały profil litologiczny wraz z danymi konstrukcyjnymi przepadłby bez komunikatu.
      const target = created.find((b) => normKey(b.designation) === normKey(det.designation));
      if (!target) continue;

      // BoreholeHousingType — osobny katalog, nie mapujemy automatycznie (zostawiamy null,
      // geolog uzupełni w edytorze otworu jeśli będzie znał typ obudowy z dokumentu).
      const housingTypeId: number | null = null;
      try {
        await boreholesApi.update(target.id, {
          designation: target.designation,
          function: target.function,
          totalDepth: det.depth ?? undefined,
          diameterMm: det.diameterMm ?? undefined,
          yieldM3h: det.yieldM3h ?? undefined,
          latitude: det.latitude ?? undefined,
          longitude: det.longitude ?? undefined,
          yearCompleted: det.yearCompleted ?? undefined,
          avgDailyIntakeM3: det.avgDailyIntakeM3 ?? undefined,
          housing: {
            typeId: housingTypeId,
            diameterMm: det.housingDiameterMm ?? null,
            depth: det.housingDepth ?? null,
            cover: det.housingCover ?? null,
            steelLadderDiameterMm: det.housingSteelLadderDiameterMm ?? null,
            hydraulicFittings: det.housingHydraulicFittings ?? null,
          },
          operat: {
            filterFromDepth: det.filterFromDepth ?? null,
            filterToDepth: det.filterToDepth ?? null,
            filterType: det.filterType ?? null,
            filterDiameterMm: det.filterDiameterMm ?? null,
            pumpType: det.pumpType ?? null,
            pumpModel: det.pumpModel ?? null,
            pumpInstallDepth: det.pumpInstallDepth ?? null,
            pumpDeliveryPipeDiameterMm: det.pumpDeliveryPipeDiameterMm ?? null,
            pumpMaxCapacityM3h: det.pumpMaxCapacityM3h ?? null,
            meterType: det.meterType ?? null,
            meterModel: det.meterModel ?? null,
            meterAccuracy: det.meterAccuracy ?? null,
            meterLocation: det.meterLocation ?? null,
            meterLastCalibration: null,
            meterNextCalibration: null,
            pumpingTestQM3h: det.pumpingTestQM3h ?? null,
            pumpingTestDrawdownM: det.pumpingTestDrawdownM ?? null,
            pumpingTestSpecificCapacity: det.pumpingTestSpecificCapacity ?? null,
            pumpingTestDurationH: det.pumpingTestDurationH ?? null,
            pumpingTestDate: null,
            permissibleDrawdownM: det.permissibleDrawdownM ?? null,
            permeabilityKMs: det.permeabilityKMs ?? null,
            transmissivityTM2s: det.transmissivityTM2s ?? null,
          },
        });
      } catch (e) {
        console.warn(`Update borehole ${target.id} failed:`, e);
      }

      // 2) Layers — fuzzy match symbolu na katalog litologii, dodaj jeden po drugim
      if (det.layers && det.layers.length > 0) {
        for (const l of det.layers) {
          if (l.fromDepth == null || l.toDepth == null) continue;
          // Match po code (case-insensitive) — LithologyType.code zwykle odpowiada symbolowi
          // litologicznemu (np. "pd", "Gπ"). Fallback na undefined gdy brak w katalogu —
          // geolog uzupełni w edytorze profilu po utworzeniu.
          const matchedLith = l.symbol
            ? lithCatalog.find(t => t.code.toLowerCase() === l.symbol!.toLowerCase())
            : undefined;
          try {
            await boreholesApi.createLayer(target.id, {
              fromDepth: l.fromDepth,
              toDepth: l.toDepth,
              symbol: l.symbol ?? '?',
              description: l.description ?? '',
              color: matchedLith?.color,
              pattern: matchedLith?.pattern,
              lithologyTypeId: matchedLith?.id,
            });
          } catch (e) {
            console.warn(`Add layer to borehole ${target.id} failed:`, e);
          }
        }
      }
    }
  }

  return (
    <div className="max-w-[1400px]">
      <header className="flex items-end justify-between mb-5">
        <div>
          <h1>Kreator ujęcia</h1>
          <div className="text-ink-400 text-[13px]">Utwórz ujęcie wody podziemnej — nowe (projektowane) lub istniejące</div>
        </div>
        <button type="button" onClick={() => setExtractModalOpen(true)} className="btn btn-secondary btn-sm">
          <Icon name="sparkles" className="w-3.5 h-3.5 text-ai-600"/>
          Wczytaj z dokumentu (AI)
        </button>
      </header>

      {aiFilledFields.size > 0 && (
        <div className="mb-3 p-2.5 rounded-md border border-ai-300 bg-ai-50 flex items-start gap-2.5 text-[12px]">
          <Icon name="sparkles" className="w-4 h-4 text-ai-600 mt-0.5 shrink-0"/>
          <div className="flex-1">
            <div className="font-semibold text-ai-800">
              {aiFilledFields.size} {aiFilledFields.size === 1 ? 'pole pre-filled' : 'pól pre-filled'} przez AI z dokumentu
            </div>
            <div className="text-ai-700 mt-0.5">
              Sprawdź wartości w każdym kroku przed zapisem. AI bywa nieomylne tylko z dobrze zeskanowanych operatów.
            </div>
          </div>
          <button onClick={() => setAiFilledFields(new Set())} className="text-ai-700 hover:text-ai-900 shrink-0"
                  title="Ukryj banner">
            <Icon name="x" className="w-3.5 h-3.5"/>
          </button>
        </div>
      )}

      <div className="flex items-center gap-2 mb-5">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className={'w-7 h-7 rounded-full grid place-items-center text-[12px] font-semibold ' + (i === 2 ? 'bg-water-500 text-white' : i < 2 ? 'bg-green-500 text-white' : 'bg-ink-100 text-ink-500')}>
              {i < 2 ? <Icon name="check" className="w-3.5 h-3.5"/> : i + 1}
            </div>
            <span className={'text-[12px] ' + (i === 2 ? 'text-ink-900 font-semibold' : 'text-ink-500')}>{s}</span>
            {i < STEPS.length - 1 && <span className="w-6 h-px bg-ink-200"/>}
          </div>
        ))}
      </div>

      {err && <div className="text-red-500 mb-3 text-[12px]">Błąd: {err}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-5">
        <div className="flex flex-col gap-4">
          <div className="card">
            <div className="card-head"><div className="card-title">Dane podstawowe</div></div>
            <div className="card-body grid grid-cols-2 gap-3">
              <label className="field col-span-2">
                <span className="field-label flex items-center justify-between">
                  <span>Klient *</span>
                  <button type="button" onClick={() => setClientModalOpen(true)} className="text-water-600 hover:text-water-700 text-[11px] font-semibold flex items-center gap-1">
                    <Icon name="plus" className="w-3 h-3"/> Nowy klient
                  </button>
                </span>
                <SearchableSelect value={clientId === '' ? '' : String(clientId)}
                                  onChange={v => setClientId(v ? Number(v) : '')}>
                  <option value="">— wybierz klienta —</option>
                  {clients.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
                </SearchableSelect>
              </label>
              <label className="field col-span-2">
                <span className="field-label">Nazwa ujęcia *</span>
                <input value={name} onChange={e => setName(e.target.value)} className="input" placeholder="np. Ujęcie Wiśniówka"/>
                {nameTaken && (
                  <span className="mt-1 text-[11px] text-red-600">
                    Ten klient ma już ujęcie o tej nazwie ({nameTaken}). Nadaj inną nazwę.
                  </span>
                )}
              </label>
              <div className="field col-span-2">
                <span className="field-label">Rodzaj ujęcia *</span>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => setKind('NEW')}
                    className={'border rounded-lg px-3 py-2 text-left cursor-pointer transition ' +
                      (kind === 'NEW' ? 'border-water-500 bg-water-500/5' : 'border-ink-200 hover:border-ink-300')}>
                    <div className="text-[13px] font-semibold text-ink-800">Nowe (projektowane)</div>
                    <div className="text-[11px] text-ink-500">Ujęcie do wykonania — projekt robót geologicznych</div>
                  </button>
                  <button type="button" onClick={() => setKind('EXISTING')}
                    className={'border rounded-lg px-3 py-2 text-left cursor-pointer transition ' +
                      (kind === 'EXISTING' ? 'border-water-500 bg-water-500/5' : 'border-ink-200 hover:border-ink-300')}>
                    <div className="text-[13px] font-semibold text-ink-800">Istniejące</div>
                    <div className="text-[11px] text-ink-500">Ujęcie już wykonane — operat / legalizacja</div>
                  </button>
                </div>
              </div>
              <label className="field"><span className="field-label">Miejscowość</span>
                <input value={town} onChange={e => setTown(e.target.value)} className="input"/></label>
              <label className="field">
                <span className="field-label">Województwo</span>
                <SearchableSelect value={voivodeship}
                        onChange={v => {
                          setVoivodeship(v);
                          // Wyczyść powiat/gminę gdy nowe województwo nie zawiera bieżąco wybranego powiatu
                          if (county && !(COUNTIES[v]?.includes(county))) { setCounty(''); setGmina(''); }
                        }}>
                  <option value="">— wybierz województwo —</option>
                  {VOIVODESHIPS.map(v => <option key={v} value={v}>{v}</option>)}
                </SearchableSelect>
              </label>
              <label className="field">
                <span className="field-label">Powiat</span>
                <SearchableSelect value={county}
                        onChange={v => { setCounty(v); setGmina(''); }}
                        disabled={!voivodeship}>
                  <option value="">{voivodeship ? '— wybierz powiat —' : '— najpierw województwo —'}</option>
                  {(COUNTIES[voivodeship] ?? []).map(c => <option key={c} value={c}>{c}</option>)}
                </SearchableSelect>
              </label>
              <label className="field">
                <span className="field-label">Gmina</span>
                <SearchableSelect value={gmina} onChange={setGmina} disabled={!county}
                        clearable allowFreeValue
                        placeholder={county ? '— wybierz gminę —' : '— najpierw powiat —'}>
                  {gminyFor(voivodeship, county).map(g => <option key={g} value={g}>{g}</option>)}
                </SearchableSelect>
              </label>
              <div className="field">
                <span className="field-label">Adres z współrzędnych</span>
                <button type="button" onClick={() => detectAddress()} disabled={geoBusy || !latitude || !longitude}
                        className="btn btn-secondary btn-sm disabled:opacity-50"
                        title="Auto po wpisaniu współrzędnych — uzupełnia miejscowość/powiat/gminę i numer działki (GUGiK ULDK). Kliknij, by wykryć ponownie.">
                  <Icon name="map" className="w-3.5 h-3.5"/> {geoBusy ? 'Wykrywanie…' : 'Wykryj z współrzędnych'}
                </button>
              </div>
              <CoordsInput
                latitude={latitude ? Number(latitude) : null}
                longitude={longitude ? Number(longitude) : null}
                onChange={(lat, lng) => {
                  setLatitude(lat != null ? String(lat) : '');
                  setLongitude(lng != null ? String(lng) : '');
                }}
                layout="stacked"
              />
              {geoInfo && (
                <div className="col-span-2 text-[11px] text-ink-600 bg-ink-50 border border-ink-100 rounded p-2">
                  {geoInfo}
                </div>
              )}
              {detectedParcel && (
                <div className="col-span-2 flex items-center gap-2 flex-wrap text-[12px] bg-emerald-50 border border-emerald-200 rounded p-2">
                  <Icon name="map" className="w-3.5 h-3.5 text-emerald-600 shrink-0"/>
                  <span className="text-ink-600">Nr działki (EGiB):</span>
                  <span className="font-mono font-semibold text-ink-800">{detectedParcel.parcelNumber ?? '—'}</span>
                  {detectedParcel.obreb && <span className="text-ink-500">· obręb {detectedParcel.obreb}</span>}
                  <span className="ml-auto text-[11px] text-emerald-700">zostanie zapisana do ujęcia</span>
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-head"><div className="card-title">Zarząd Zlewni Wód Polskich</div>
              <span className="ml-auto text-[11px] text-ink-400">do wniosku wodnoprawnego</span>
            </div>
            <div className="card-body space-y-2">
              <label className="field block">
                <span className="field-label">Zarząd Zlewni</span>
                <SearchableSelect
                  value={zarzadZlewniId === '' ? '' : String(zarzadZlewniId)}
                  onChange={v => setZarzadZlewniId(v === '' ? '' : Number(v))}
                >
                  <option value="">— nie wybrany —</option>
                  {(() => {
                    const groups: Record<string, ZarzadZlewniDto[]> = {};
                    zzList.forEach(z => {
                      if (!groups[z.rzgwName]) groups[z.rzgwName] = [];
                      groups[z.rzgwName].push(z);
                    });
                    return Object.keys(groups).sort().map(rzgw => (
                      <optgroup key={rzgw} label={rzgw}>
                        {groups[rzgw].map(z => (
                          <option key={z.id} value={z.id}>{z.name}</option>
                        ))}
                      </optgroup>
                    ));
                  })()}
                </SearchableSelect>
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                <button type="button" onClick={detectZarzadZlewni}
                        disabled={!latitude || !longitude}
                        className="btn btn-secondary btn-sm"
                        title="Heurystyka: podpowiada ZZ na podstawie województwa. Pełne dopasowanie zlewniowe sprawdź na hydroportal.isok.gov.pl">
                  <Icon name="map" className="w-3.5 h-3.5"/> Wykryj z współrzędnych
                </button>
                {zarzadZlewniId !== '' && (() => {
                  const z = zzList.find(x => x.id === zarzadZlewniId);
                  if (!z) return null;
                  return (
                    <span className="text-[11px] text-ink-500">
                      {z.rzgwName}
                      {z.addressLine1 && <> · <span className="font-mono">{z.addressLine1}, {z.addressPostalCode} {z.addressCity}</span></>}
                    </span>
                  );
                })()}
              </div>
              {zzDetectInfo && (
                <div className="text-[11px] text-ink-600 bg-ink-50 border border-ink-100 rounded p-2">
                  {zzDetectInfo}
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <div className="card-title">Wydajność Q</div>
              <span className="ml-auto text-[11px] text-ink-400">opcjonalna</span>
            </div>
            <div className="card-body">
              <div className="flex items-baseline gap-2 mb-2">
                <input type="number" value={q} min={0}
                       onChange={e => setQ(e.target.value === '' ? '' : Number(e.target.value))}
                       placeholder="nie podano" className="input w-28 font-mono"/>
                <span className="text-ink-500 text-[13px]">m³/h</span>
              </div>
              {q === '' && (
                <div className="text-[11px] text-ink-400 mb-1">
                  Bez Q system nie proponuje decydenta — wybierz go ręcznie poniżej (domyślnie Starosta).
                </div>
              )}
              <div className="relative w-full h-2.5 bg-ink-100 rounded-full overflow-hidden">
                <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-water-500 to-water-600 transition-all" style={{ width: qPct + '%' }}/>
                <div className="absolute inset-y-0 left-1/2 w-px bg-red-500"/>
              </div>
              <div className="flex justify-between text-[10px] text-ink-400 mt-1">
                <span>0</span>
                <span className="text-red-500 font-semibold">50 m³/h (próg marszałka)</span>
                <span>100</span>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-4">
                <button
                  onClick={() => setAuthorityOverride(decydentOverride === 'DISTRICT' ? '' : 'DISTRICT')}
                  className={'border rounded-lg p-3 text-left cursor-pointer transition ' +
                    (effectiveAuthority === 'DISTRICT' ? 'border-water-500 bg-water-500/5' : 'border-ink-200 hover:border-ink-300')}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="pill pill-star"><span className="pill-dot"/>Starosta</span>
                    {propozycjaAuthority === 'DISTRICT' && <span className="text-[10px] text-ai-600 font-semibold">propozycja</span>}
                  </div>
                  <div className="text-[11px] text-ink-500">Q ≤ 50 m³/h · kategoria II/III</div>
                </button>
                <button
                  onClick={() => setAuthorityOverride(decydentOverride === 'REGIONAL' ? '' : 'REGIONAL')}
                  className={'border rounded-lg p-3 text-left cursor-pointer transition ' +
                    (effectiveAuthority === 'REGIONAL' ? 'border-water-500 bg-water-500/5' : 'border-ink-200 hover:border-ink-300')}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="pill pill-marsz"><span className="pill-dot"/>Marszałek</span>
                    {propozycjaAuthority === 'REGIONAL' && <span className="text-[10px] text-ai-600 font-semibold">propozycja</span>}
                  </div>
                  <div className="text-[11px] text-ink-500">Q &gt; 50 m³/h · kategoria IV/V</div>
                </button>
              </div>

              {nadpisano && (
                <label className="field mt-3">
                  <span className="field-label">Uzasadnienie nadpisania *</span>
                  <textarea value={justification} onChange={e => setUzasadnienie(e.target.value)} className="textarea" rows={3} placeholder="Wyjaśnij, dlaczego wybrano innego decydenta niż proponuje system…"/>
                </label>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <div className="card-title">Planowane otwory</div>
            </div>
            <div className="card-body !p-0">
              <table className="table-hw">
                <thead><tr><th>Oznaczenie</th><th>Funkcja</th><th>Głębokość [m]</th><th>Szer. geogr.</th><th>Dług. geogr.</th><th></th></tr></thead>
                <tbody>
                  {boreholes.map((o, i) => (
                    <tr key={i}>
                      <td>
                        <input value={o.designation} onChange={e => updBorehole(i, { designation: e.target.value })}
                               className={'input !py-1 font-mono text-[12px]' + (repeatedBoreholes.has(i) ? ' !border-red-400 bg-red-50' : '')}/>
                      </td>
                      <td>
                        <SearchableSelect value={o.function} onChange={v => updBorehole(i, { function: v })} className="!py-1">
                          <option value="PRODUCTION">Eksploatacyjny</option>
                          <option value="OBSERVATION">Obserwacyjny</option>
                          <option value="REPLACEMENT">Zastępczy</option>
                          <option value="INACTIVE">Nieczynny</option>
                        </SearchableSelect>
                      </td>
                      <td><input type="number" value={o.depth ?? ''} onChange={e => updBorehole(i, { depth: e.target.value ? Number(e.target.value) : undefined })} className="input !py-1 w-24 font-mono text-[12px]"/></td>
                      <td><input inputMode="decimal" value={o.latitude ?? ''} placeholder="52.23456"
                                 onChange={e => updBorehole(i, { latitude: sanitizeDecimalInput(e.target.value) || undefined })}
                                 className="input !py-1 w-28 font-mono text-[12px]"/></td>
                      <td><input inputMode="decimal" value={o.longitude ?? ''} placeholder="21.01234"
                                 onChange={e => updBorehole(i, { longitude: sanitizeDecimalInput(e.target.value) || undefined })}
                                 className="input !py-1 w-28 font-mono text-[12px]"/></td>
                      <td><button onClick={() => rmBorehole(i)} className="btn btn-ghost btn-icon text-ink-400 hover:text-red-500"><Icon name="x"/></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {repeatedBoreholes.size > 0 && (
                <div className="px-3 pt-2 text-[11px] text-red-600">
                  Powtórzone oznaczenia — każdy otwór w ujęciu musi mieć inne.
                </div>
              )}
              <div className="px-3 py-2 border-t border-ink-100">
                <button onClick={addBorehole} className="btn btn-secondary btn-sm"><Icon name="plus" className="w-3.5 h-3.5"/> Dodaj</button>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button onClick={() => nav('/intakes')} className="btn btn-secondary">Anuluj</button>
            <button disabled={busy} onClick={submit} className="btn btn-primary">{busy ? 'Tworzenie…' : 'Utwórz ujęcie'}</button>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="card border-ai-500/30">
            <div className="card-head">
              <div className="flex items-center gap-2">
                <div className="sdot sdot-ai sdot-live"/>
                <div className="card-title">Propozycja systemu</div>
              </div>
            </div>
            <div className="card-body">
              {!proposal && <div className="text-ink-400 text-[12px]">Wpisz wydajność Q…</div>}
              {proposal && (
                <>
                  <div className="mb-3">
                    <span className={'pill ' + (proposal.authority === 'REGIONAL' ? 'pill-marsz' : 'pill-star')}>
                      <span className="pill-dot"/>{proposal.authorityName}
                    </span>
                  </div>
                  <div className="flex flex-col gap-2 text-[12px]">
                    <div><span className="text-ink-400">Wymagana kategoria:</span> <span className="font-mono font-semibold text-ink-800">{proposal.category}</span></div>
                    <div><span className="text-ink-400">Typowy czas:</span> <span className="font-mono font-semibold text-ink-800">{proposal.typicalDays} dni</span></div>
                    <div><span className="text-ink-400">Próg:</span> <span className="font-mono text-ink-600">{proposal.threshold} m³/h</span></div>
                  </div>
                  <hr className="border-0 border-t border-ink-100 my-3"/>
                  <div className="text-[12px] text-ink-600">{proposal.justification}</div>
                  {nadpisano && (
                    <div className="mt-3 p-2 bg-amber-500/10 border border-amber-500/30 rounded text-[11px] text-amber-700">
                      Decydent nadpisany — wymaga uzasadnienia.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <div className="card-title">Lokalizacja na mapie</div>
              {previewIntake && (
                <span className="ml-auto text-[11px] text-ink-500 font-mono">
                  {Number(previewIntake.latitude).toFixed(5)}, {Number(previewIntake.longitude).toFixed(5)}
                </span>
              )}
            </div>
            <div className="card-body !p-0">
              {previewIntake ? (
                <MapLibreMap
                  intake={previewIntake}
                  center={previewCenter}
                  zoom={14}
                  height="320px"
                />
              ) : (
                <div className="p-6 text-center text-[12px] text-ink-400">
                  <Icon name="map" className="w-8 h-8 mx-auto mb-2 text-ink-300"/>
                  Wpisz <strong>szerokość</strong> i <strong>długość geograficzną</strong> obok,<br/>
                  żeby zobaczyć ujęcie na mapie.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ClientFormModal
        open={clientModalOpen}
        initial={null}
        onClose={() => setClientModalOpen(false)}
        onSaved={(c) => { setClients([...clients.filter(x => x.id !== c.id), c].sort((a, b) => a.name.localeCompare(b.name))); setClientId(c.id); }}
      />

      <IntakeDocumentExtractModal
        open={extractModalOpen}
        onClose={() => setExtractModalOpen(false)}
        onApply={(data) => {
          applyExtractedToWizard(data);
          setExtractModalOpen(false);
        }}
      />
    </div>
  );

  // === Pre-fill kreatora wartościami z AI ===
  function applyExtractedToWizard(data: ExtractedIntakeData) {
    const filled = new Set<string>();
    if (data.name) { setName(data.name); filled.add('name'); }
    if (data.town) { setTown(data.town); filled.add('town'); }
    if (data.county) { setCounty(data.county); filled.add('county'); }
    if (data.voivodeship) { setVoivodeship(data.voivodeship); filled.add('voivodeship'); }
    if (data.latitude != null) { setLatitude(String(data.latitude)); filled.add('latitude'); }
    if (data.longitude != null) { setLongitude(String(data.longitude)); filled.add('longitude'); }
    if (data.qM3h != null) { setQ(Number(data.qM3h)); filled.add('qM3h'); }
    if (data.boreholes && data.boreholes.length > 0) {
      // Wizard rejestruje tylko minimum (designation, function, depth=totalDepth) —
      // bo IntakeWizardService.save tworzy PlannedBorehole. Pełne dane (konstrukcja,
      // hydraulika, profile litologiczne) trzymamy w pendingBoreholeDetails i backfillujemy
      // PUT/POST'ami po wizard.save (gdy mamy borehole IDs).
      // Oznaczenia zastępcze dla otworów, dla których AI go nie odczytała, muszą omijać te,
      // które odczytała — inaczej „H-02" dorobione dla drugiego otworu zderza się z „H-02"
      // wyciągniętym dla trzeciego, a backfill dopina dane AI właśnie po oznaczeniu.
      const uzyte = data.boreholes.map(b => b.designation).filter(Boolean) as string[];
      const withDesignation = data.boreholes.map((b) => {
        if (b.designation) return { ...b, designation: b.designation };
        const wolne = nextFreeDesignation(uzyte);
        uzyte.push(wolne);
        return { ...b, designation: wolne };
      });
      setBoreholes(withDesignation.map(b => ({
        designation: b.designation,
        function: b.function ?? 'PRODUCTION',
        depth: b.depth ?? undefined,
      })));
      setPendingBoreholeDetails(withDesignation);
      filled.add('boreholes');
      const layerCount = data.boreholes.reduce((s, b) => s + (b.layers?.length ?? 0), 0);
      if (layerCount > 0) filled.add(`layers (${layerCount})`);
    }
    setAiFilledFields(filled);
  }
}
